import { type RootState } from "../../../../../../redux/store";
import { selectProductDescriptor } from "../../../../../../redux/products/productsSelectors";
import { batch, useDispatch, useSelector } from "react-redux";
import { type ThunkAction } from "redux-thunk";
import { type AnyAction } from "../../../../../../redux/types";
import {
  selectIsCartAboveThreshold,
  selectLineItemsByProduct,
} from "~/feature/cart/cart/redux/selectors/cartSelectors";
import { loadPnACartEntry } from "~/feature/cart/cart/redux/actions/cartPageActions";
import { LinkButton } from "~/common/components/Link";
import { useTranslation } from "react-i18next";
import AutorenewOutlinedIcon from "@mui/icons-material/AutorenewOutlined";
import { MenuItem } from "@mui/material";

interface Props {
  className?: string;
  menuClassName?: string;
}

export const LoadAllPnAButton = ({ className, menuClassName }: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const isAboveThreshold = useSelector(selectIsCartAboveThreshold);
  const isProductNeedsLoading = useSelector(selectSomeProductNeedsLoading);
  const isProductNotLoaded = useSelector(selectSomeProductIsNotLoaded);

  if (!isAboveThreshold || !isProductNotLoaded) {
    return null;
  }

  return (
    <MenuItem className={menuClassName}>
      <LinkButton
        iconBefore={<AutorenewOutlinedIcon />}
        onClick={() => dispatch(loadAllPnAAction())}
        data-test-id={"btn-load-all-pna"}
        className={className}
        disabled={!isProductNeedsLoading}
      >
        {t("cartPage.btn.loadAllPnA")}
      </LinkButton>
    </MenuItem>
  );
};

const selectSomeProductNeedsLoading = (state: RootState) => {
  return Object.values(state.cart.packEntriesById).some((pack) => {
    const productDescriptor = selectProductDescriptor(state, {
      productCode: pack.productCode,
      priceReference: pack.priceReference,
    });
    return !productDescriptor.isLoaded && !productDescriptor.isLoading;
  });
};
const selectSomeProductIsNotLoaded = (state: RootState) => {
  return Object.values(state.cart.packEntriesById).some((pack) => {
    const productDescriptor = selectProductDescriptor(state, {
      productCode: pack.productCode,
      priceReference: pack.priceReference,
    });
    return !productDescriptor.isLoaded;
  });
};

type ThunkResult<R> = ThunkAction<R, RootState, undefined, AnyAction>;

function loadAllPnAAction(): ThunkResult<void> {
  return (dispatch, getState) => {
    const state = getState();

    const productCodes = Object.keys(selectLineItemsByProduct(state));

    batch(() => {
      productCodes.forEach((productCode) => dispatch(loadPnACartEntry(productCode)));
    });
  };
}
